import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Stack,
  TextField,
  Button,
  Divider,
  Avatar,
  Grid,
  alpha,
  useTheme,
  Chip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import LockIcon from "@mui/icons-material/Lock";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { GlobalContext } from "../../context/GlobalContext";
import { TranslateContext } from "../../context/TranslateContext";
import PageHeader from "../../common/PageHeader";

const UserProfile = () => {
  const { hitAxios } = React.useContext(GlobalContext);
  const { lang } = React.useContext(TranslateContext);
  const theme = useTheme();
  const [profile, setProfile] = useState(null);
  const [form, setForm] = useState({ name: "", email: "" });
  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: "",
  });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    const res = await hitAxios({
      path: "/api/user/profile",
      showSnackbar: false,
      admin: false,
    });
    if (res?.data?.success) {
      setProfile(res.data.data);
      setForm({ name: res.data.data.name, email: res.data.data.email });
    }
  };
  const saveProfile = async () => {
    await hitAxios({
      path: "/api/user/update-profile",
      post: true,
      obj: form,
      admin: false,
    });
    loadProfile();
  };
  const changePassword = async () => {
    if (passwordForm.new_password !== passwordForm.confirm_password) return;
    await hitAxios({
      path: "/api/user/change-password",
      post: true,
      obj: passwordForm,
      admin: false,
    });
    setPasswordForm({
      current_password: "",
      new_password: "",
      confirm_password: "",
    });
  };

  return (
    <Box>
      <PageHeader
        title={lang.profileSettings || "Profile Settings"}
        subtitle="Manage your account information"
        icon={AccountCircleIcon}
      />

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 5 }}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              {/* Avatar Section */}
              <Box
                sx={{
                  textAlign: "center",
                  py: 3,
                  mb: 2,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha(theme.palette.primary.main, 0.03)} 100%)`,
                }}
              >
                <Avatar
                  sx={{
                    width: 80,
                    height: 80,
                    margin: "0 auto 12px",
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.main, 0.7)} 100%)`,
                    fontSize: 32,
                    fontWeight: 700,
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.4)}`,
                  }}
                >
                  {profile?.name?.[0]?.toUpperCase() || "U"}
                </Avatar>
                <Typography variant="h6" fontWeight={700}>
                  {profile?.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {profile?.email}
                </Typography>
                {profile?.plan_name && (
                  <Chip
                    label={profile.plan_name}
                    size="small"
                    color="primary"
                    sx={{ mt: 1, fontWeight: 600 }}
                  />
                )}
              </Box>

              <Divider sx={{ mb: 2.5 }} />

              <Typography
                variant="subtitle2"
                fontWeight={700}
                mb={2}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <EditIcon sx={{ fontSize: 16 }} />{" "}
                {lang.updateProfile || "Update Profile"}
              </Typography>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  size="small"
                  label={lang.fullName || "Full Name"}
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                />
                <TextField
                  fullWidth
                  size="small"
                  label={lang.email || "Email"}
                  value={form.email}
                  disabled
                  helperText="Email cannot be changed"
                />
                <Button
                  variant="contained"
                  onClick={saveProfile}
                  sx={{ borderRadius: 2, alignSelf: "flex-start" }}
                >
                  {lang.saveChanges || "Save Changes"}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 7 }}>
          <Card>
            <CardContent sx={{ p: 3 }}>
              <Typography
                variant="subtitle2"
                fontWeight={700}
                mb={2}
                sx={{ display: "flex", alignItems: "center", gap: 1 }}
              >
                <LockIcon sx={{ fontSize: 16 }} />{" "}
                {lang.changePassword || "Change Password"}
              </Typography>
              <Stack spacing={2}>
                <TextField
                  fullWidth
                  size="small"
                  type="password"
                  label={lang.currentPassword || "Current Password"}
                  value={passwordForm.current_password}
                  onChange={(e) =>
                    setPasswordForm({
                      ...passwordForm,
                      current_password: e.target.value,
                    })
                  }
                />
                <TextField
                  fullWidth
                  size="small"
                  type="password"
                  label={lang.newPassword || "New Password"}
                  value={passwordForm.new_password}
                  onChange={(e) =>
                    setPasswordForm({
                      ...passwordForm,
                      new_password: e.target.value,
                    })
                  }
                />
                <TextField
                  fullWidth
                  size="small"
                  type="password"
                  label={lang.confirmNewPassword || "Confirm New Password"}
                  value={passwordForm.confirm_password}
                  onChange={(e) =>
                    setPasswordForm({
                      ...passwordForm,
                      confirm_password: e.target.value,
                    })
                  }
                  error={
                    !!passwordForm.confirm_password &&
                    passwordForm.new_password !== passwordForm.confirm_password
                  }
                  helperText={
                    !!passwordForm.confirm_password &&
                    passwordForm.new_password !== passwordForm.confirm_password
                      ? "Passwords do not match"
                      : ""
                  }
                />
                <Button
                  variant="contained"
                  color="warning"
                  onClick={changePassword}
                  disabled={
                    !passwordForm.current_password ||
                    !passwordForm.new_password ||
                    passwordForm.new_password !== passwordForm.confirm_password
                  }
                  sx={{ borderRadius: 2, alignSelf: "flex-start" }}
                >
                  {lang.updatePassword || "Update Password"}
                </Button>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default UserProfile;
